import { Link } from "react-router-dom";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Text,
  Flex,
  Stack,
  Box,
} from "@chakra-ui/react";
import { useActive } from "../hooks/useActive.hook";
import PropTypes from "prop-types";

const SidebarTab = ({ data }) => {
  const { heading, icon, submenu, rootPath } = data;
  const { isActive } = useActive();
  const renderedIcon = typeof icon === "function" ? icon() : icon;

  const getSubmenuMatchPath = (item) => item.activePath || item.path;

  // Pick only the most specific active submenu to avoid multiple highlights
  const activeSubmenuPath =
    submenu?.reduce((bestMatch, item) => {
      const currentMatchPath = getSubmenuMatchPath(item);
      if (!isActive(currentMatchPath, "normal")) return bestMatch;

      if (!bestMatch || currentMatchPath.length > bestMatch.length) {
        return currentMatchPath;
      }

      return bestMatch;
    }, "") || "";

  // Use exact match for root path, includes for others
  const activeType = rootPath === "/" ? "normal" : "includes";
  const isRootActive =
    isActive(rootPath, activeType) ||
    Boolean(activeSubmenuPath);

  // 🔹 CASE 1: NO SUBMENU (Simple link)
  if (!submenu || submenu.length === 0) {
    return (
      <Link to={rootPath} style={{ textDecoration: "none" }}>
        <Flex
          alignItems="center"
          gap={3}
          px={4}
          py={2}
          mb={3}
          bg={isRootActive ? "brand.secondary" : "brand.accent"}
          color="white"
          fontWeight={isRootActive ? "bold" : "normal"}
          borderLeft={isRootActive ? "4px solid white" : undefined}
        >
          {/* ICON */}
          <Box fontSize={20}>{renderedIcon}</Box>

          {/* HEADING */}
          <Text>{heading}</Text>
        </Flex>
      </Link>
    );
  }

  // 🔹 CASE 2: WITH SUBMENU
  return (
    <AccordionItem mb={3} border="none">
      <AccordionButton
        _hover={{ bg: "brand.secondary" }}
        bg={isRootActive ? "brand.secondary" : "brand.accent"}
        color="white"
        px={4}
        py={2}
        fontWeight={isRootActive ? "bold" : "normal"}
      >
        <Flex gap={3} alignItems="center" flex="1" textAlign="left">
          <Box fontSize={20}>{renderedIcon}</Box>
          <Text>{heading}</Text>
        </Flex>
        <AccordionIcon />
      </AccordionButton>

      <AccordionPanel p={2}>
        <Stack spacing={1}>
          {submenu.map(({ label, path, activePath }, index) => {
            const currentMatchPath = activePath || path;
            const isSubActive = activeSubmenuPath === currentMatchPath;

            return (
              <Link
                to={path}
                key={`${label}-${index}`}
                style={{ textDecoration: "none" }}
              >
                <Text
                  fontSize={14}
                  pl={8}
                  pr={2}
                  py={2}
                  display="block"
                  color="white"
                  bg={isSubActive ? "brand.secondary" : "transparent"}
                  fontWeight={isSubActive ? "bold" : "normal"}
                  _hover={{
                    bg: "brand.secondary",
                    color: "white",
                  }}
                >
                  {label}
                </Text>
              </Link>
            );
          })}
        </Stack>
      </AccordionPanel>
    </AccordionItem>
  );
};

SidebarTab.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
    rootPath: PropTypes.string.isRequired,
    submenu: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
        activePath: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export { SidebarTab };
